import React from "react";
import { motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import SEOHead from "../components/common/SEOHead";
import { getSEOData } from "../utils/seoConfig";

const Prizes = () => {
  const seoData = getSEOData("prizes");

  const categories = [
    {
      name: "School Category",
      description: "For students currently enrolled in secondary education",
      icon: <ion-icon name="library"></ion-icon>,
      prizes: {
        overall: { first: 30000, second: 10000, third: 10000 },
        specialAwards: {
          bestDesign: 20000,
          bestAudioVisual: 20000,
          peoplesChoice: 10000,
        },
      },
    },
    {
      name: "University Category",
      description: "For university students and recent graduates",
      icon: <ion-icon name="school"></ion-icon>,
      prizes: {
        overall: { first: 30000, second: 10000, third: 10000 },
        specialAwards: {
          bestDesign: 20000,
          bestAudioVisual: 20000,
          peoplesChoice: 10000,
        },
      },
    },
  ];

  const overallChampionship = {
    title: "EXE Overall Champion",
    description: "Best game across all categories",
    prize: "Trophy",
    icon: <ion-icon name="trophy"></ion-icon>,
  };

  const publishingOpportunity = {
    title: "Publishing Opportunity",
    description: "Top 5 games overall receive publishing support",
    amount: "LKR 10,000/month for 6 months",
    details: "60,000 LKR per game to develop prototype to release-ready state",
    icon: <ion-icon name="cloud-upload"></ion-icon>,
  };

  const totalBreakdown = [
    {
      category: "School Category Winners",
      amount: "100,000",
      breakdown: "30K + 20K + 20K + 10K + 10K + 10K",
    },
    {
      category: "University Category Winners",
      amount: "100,000",
      breakdown: "30K + 20K + 20K + 10K + 10K + 10K",
    },
    // {
    //   category: "Publishing Support (5 games)",
    //   amount: "300,000",
    //   breakdown: "60K x 5 games",
    // },
  ];

  return (
    <HelmetProvider>
      <div className="exe-bg-pattern min-h-screen pt-20">
        <SEOHead
          title={seoData.title}
          description={seoData.description}
          image={seoData.image}
          url={seoData.url}
          keywords={seoData.keywords}
        />

        <div className="exe-container py-16">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-6xl mx-auto"
          >
            {/* Header */}
            <div className="text-center mb-16">
              <h1 className="exe-title text-exe-text-accent mb-6">
                Prizes & Awards
              </h1>
              <div className="h-1 w-24 bg-exe-gradient-primary mx-auto mb-8"></div>
              <p className="exe-text text-xl max-w-3xl mx-auto">
                LKR 200,000 total prize pool 
              </p>
              {/* <p className="exe-text text-xl max-w-3xl mx-auto">
                with additional publishing
                opportunities worth LKR 300,000 for the top 5 games.
              </p> */}
            </div>

            {/* Overall Championship */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <div className="exe-glass-card p-8 border-exe-text-accent border-2 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-exe-gradient-primary opacity-10 rounded-full -mr-16 -mt-16"></div>

                <div className="text-center relative z-10">
                  <div className="text-6xl mb-4">
                    {overallChampionship.icon}
                  </div>
                  <h2 className="exe-heading text-exe-text-accent mb-2">
                    {overallChampionship.title}
                  </h2>
                  <div className="text-2xl font-bold text-exe-text-accent mb-4">
                    {overallChampionship.prize}
                  </div>
                  <p className="exe-text mb-6">
                    {overallChampionship.description}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Category Prizes */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <h2 className="exe-heading text-exe-text-accent text-center mb-8">
                Competition Categories
              </h2>

              <div className="grid lg:grid-cols-2 gap-8">
                {categories.map((category, index) => (
                  <div key={category.name} className="exe-glass-card p-8">
                    <div className="text-center mb-6">
                      <div className="text-4xl mb-3">{category.icon}</div>
                      <h3 className="text-2xl font-bold text-exe-text-primary mb-2">
                        {category.name}
                      </h3>
                      <p className="exe-text text-sm text-exe-text-muted">
                        {category.description}
                      </p>
                    </div>

                    {/* Overall Winners */}
                    <div className="mb-6">
                      <h4 className="font-semibold text-exe-text-accent mb-4 text-center">
                        Overall Winners
                      </h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                          <div className="text-2xl mb-1">🥇</div>
                          <div className="font-bold text-exe-text-accent text-sm">
                            1st Place
                          </div>
                          <div className="exe-text text-sm">
                            LKR {category.prizes.overall.first.toLocaleString()}
                          </div>
                        </div>

                        <div className="text-center">
                          <div className="text-2xl mb-1">🥈</div>
                          <div className="font-bold text-exe-text-primary text-sm">
                            2nd Place
                          </div>
                          <div className="exe-text text-sm">
                            LKR{" "}
                            {category.prizes.overall.second.toLocaleString()}
                          </div>
                        </div>

                        <div className="text-center">
                          <div className="text-2xl mb-1">🥉</div>
                          <div className="font-bold text-exe-text-secondary text-sm">
                            3rd Place
                          </div>
                          <div className="exe-text text-sm">
                            LKR {category.prizes.overall.third.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Special Awards */}
                    <div>
                      <h4 className="font-semibold text-exe-text-accent mb-4 text-center">
                        Special Awards
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="exe-text text-sm">
                            <ion-icon name="color-palette"></ion-icon> Best Game
                            Design
                          </span>
                          <span className="font-bold text-exe-text-accent">
                            LKR{" "}
                            {category.prizes.specialAwards.bestDesign.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="exe-text text-sm">
                            <ion-icon name="musical-notes"></ion-icon> Best
                            Audio & Visual
                          </span>
                          <span className="font-bold text-exe-text-accent">
                            LKR{" "}
                            {category.prizes.specialAwards.bestAudioVisual.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="exe-text text-sm">
                            <ion-icon name="heart"></ion-icon> People's Choice
                          </span>
                          <span className="font-bold text-exe-text-accent">
                            LKR{" "}
                            {category.prizes.specialAwards.peoplesChoice.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Category Winner Trophy */}
                    <div className="mt-6 pt-4 border-t border-exe-text-accent/20 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-lg">
                          <ion-icon name="trophy"></ion-icon>
                        </span>
                        <span className="exe-text text-sm font-semibold">
                          Category Champion Trophy
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Publishing Opportunity 
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <div className="exe-glass-card p-8 border-exe-text-accent border relative overflow-hidden">
                <div className="absolute top-0 left-0 w-32 h-32 bg-exe-gradient-primary opacity-10 rounded-full -ml-16 -mt-16"></div>

                <div className="text-center relative z-10">
                  <div className="text-5xl mb-4">
                    {publishingOpportunity.icon}
                  </div>
                  <h2 className="exe-heading text-exe-text-accent mb-2">
                    {publishingOpportunity.title}
                  </h2>
                  <div className="text-xl font-bold text-exe-text-accent mb-2">
                    {publishingOpportunity.amount}
                  </div>
                  <p className="exe-text mb-4">
                    {publishingOpportunity.description}
                  </p>
                  <p className="exe-text text-sm text-exe-text-muted">
                    {publishingOpportunity.details}
                  </p>
                </div>
              </div>
            </motion.div>*/}

            {/* Prize Breakdown */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <div className="exe-glass-card p-8">
                <h2 className="exe-heading text-exe-text-accent text-center mb-8">
                  Prize Pool Breakdown
                </h2>

                <div className="space-y-4 mb-8">
                  {totalBreakdown.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-4 bg-exe-bg-secondary/30 rounded-lg"
                    >
                      <div>
                        <span className="font-semibold text-exe-text-primary">
                          {item.category}
                        </span>
                        <div className="text-sm text-exe-text-muted">
                          {item.breakdown}
                        </div>
                      </div>
                      <span className="text-xl font-bold text-exe-text-accent">
                        LKR {item.amount}
                      </span>
                    </motion.div>
                  ))}
                </div>

                <div className="text-center pt-6 border-t border-exe-text-accent/20">
                  <div className="text-sm text-exe-text-muted mb-2">
                    Total Prize Pool
                  </div>
                  <div className="text-4xl font-bold text-exe-text-accent mb-2">
                    LKR 200,000
                  </div>
                  {/* <div className="text-sm text-exe-text-muted mb-4">
                    + LKR 300,000 in publishing support
                  </div> 
                  <div className="text-2xl font-bold text-exe-text-accent">
                    LKR 200,000 Total Value
                  </div>*/}
                </div>
              </div>
            </motion.div>

            {/* Trophies and Recognition */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="exe-glass-card p-8">
                <h2 className="exe-heading text-exe-text-accent mb-6">
                  Trophies & Recognition
                </h2>

                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-4xl mb-3"><ion-icon name="trophy"></ion-icon></div>
                    <h3 className="font-semibold text-exe-text-primary mb-2">
                      EXE Overall Champion
                    </h3>
                    <p className="exe-text text-sm">
                      Best game across all categories
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="text-4xl mb-3"><ion-icon name="sparkles"></ion-icon></div>
                    <h3 className="font-semibold text-exe-text-primary mb-2">
                      Category Champions
                    </h3>
                    <p className="exe-text text-sm">
                      Winners of School & University categories
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="text-4xl mb-3"><ion-icon name="medal"></ion-icon></div>
                    <h3 className="font-semibold text-exe-text-primary mb-2">
                      All Participants
                    </h3>
                    <p className="exe-text text-sm">
                      Certificate of participation
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <motion.a
                    href="/rules"
                    className="exe-btn exe-btn-primary"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    View Competition Rules
                  </motion.a>

                  <motion.a
                    href="/contact"
                    className="exe-btn exe-btn-outline"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Contact Us
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </HelmetProvider>
  );
};

export default Prizes;
